import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchDiagnostic, submitAssessment } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { LegalFooter } from "@/components/LegalFooter";
import { BrandLockup } from "@/components/BrandLockup";

const LikertRow = ({ question, scale, value, onSelect }) => (
  <div className="border-t border-[#E2DFD8] pt-8" data-testid={`question-${question.id}`}>
    <p className="text-base md:text-lg text-[#0B0E14] leading-relaxed mb-6 max-w-2xl">{question.text}</p>
    <div className="grid grid-cols-5 gap-2 md:gap-3">
      {scale.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onSelect(question.id, opt.value)}
            data-testid={`option-${question.id}-${opt.value}`}
            className={`flex flex-col items-center justify-start text-center px-1 py-4 border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A68A56] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F6F5F2] ${
              selected
                ? "bg-[#0B0E14] text-white border-[#0B0E14]"
                : "bg-white text-[#4A5568] border-[#E2DFD8] hover:border-[#0B0E14]"
            }`}
          >
            <span className="font-display text-lg font-medium">{opt.value}</span>
            <span className={`text-[10px] md:text-xs mt-2 leading-tight ${selected ? "text-white/80" : "text-[#718096]"}`}>
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

export default function Diagnostic() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [content, setContent] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [baseline, setBaseline] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Refetch localized content when language changes. Answers are keyed by
  // question id (language-independent) so they are preserved across switches.
  useEffect(() => {
    fetchDiagnostic(lang)
      .then(setContent)
      .catch(() => toast.error(t.diagnostic.loadError));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const totalSteps = useMemo(() => (content ? content.dimensions.length + 1 : 1), [content]);
  const isBaselineStep = content && step === content.dimensions.length;

  const select = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));
  const selectBaseline = (id, value) => setBaseline((b) => ({ ...b, [id]: value }));

  const currentDimension = content && !isBaselineStep ? content.dimensions[step] : null;
  const dimComplete =
    currentDimension && currentDimension.questions.every((q) => answers[q.id] !== undefined);

  const progress = Math.round(((step + (isBaselineStep ? 1 : dimComplete ? 0.99 : 0)) / totalSteps) * 100);

  const next = () => {
    if (currentDimension && !dimComplete) {
      toast.message(t.diagnostic.answerAll);
      return;
    }
    setStep((s) => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      const data = await submitAssessment(answers, baseline, lang);
      navigate(`/results/${data.id}`, { state: { result: data.result } });
    } catch (e) {
      toast.error(t.diagnostic.submitError);
      setSubmitting(false);
    }
  };

  if (!content) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#A68A56] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F2] flex flex-col">
      <header className="border-b border-[#E2DFD8] bg-[#F6F5F2]/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <span data-testid="brand-header">
            <BrandLockup />
          </span>
          <div className="flex items-center gap-5">
            <span className="text-xs uppercase tracking-[0.18em] text-[#718096] hidden sm:inline" data-testid="step-indicator">
              {isBaselineStep ? t.diagnostic.finalStep : t.diagnostic.sectionOf(step + 1, content.dimensions.length)}
            </span>
            <LanguageToggle />
          </div>
        </div>
        <div className="h-1 bg-[#E2DFD8] w-full">
          <div
            className="h-full bg-[#0B0E14]"
            style={{ width: `${progress}%`, transition: "width 0.4s ease-out" }}
            data-testid="progress-fill"
          />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-6 md:px-12 py-12 md:py-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {!isBaselineStep ? (
              <>
                {step === 0 && (
                  <div className="mb-10 border border-[#E2DFD8] bg-white p-5 md:p-6" data-testid="privacy-notice">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A68A56] mb-2">{t.privacyNotice.title}</p>
                    <p className="text-sm text-[#4A5568] leading-relaxed">{t.privacyNotice.body}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs">
                      <Link to="/privacy" data-testid="notice-privacy-link" className="text-[#0B0E14] underline underline-offset-2 hover:text-[#A68A56] transition-colors">{t.footer.privacy}</Link>
                      <Link to="/terms" data-testid="notice-terms-link" className="text-[#0B0E14] underline underline-offset-2 hover:text-[#A68A56] transition-colors">{t.footer.terms}</Link>
                    </div>
                  </div>
                )}
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56] mb-4">
                  {t.diagnostic.dimensionOf(step + 1, content.dimensions.length)}
                </p>
                <h2 className="font-display text-2xl md:text-4xl font-normal tracking-tight text-[#0B0E14]">
                  {currentDimension.name}
                </h2>
                <p className="text-base text-[#4A5568] mt-3 max-w-2xl">{currentDimension.intro}</p>
                <div className="mt-12 flex flex-col gap-10">
                  {currentDimension.questions.map((q) => (
                    <LikertRow
                      key={q.id}
                      question={q}
                      scale={content.scale}
                      value={answers[q.id]}
                      onSelect={select}
                    />
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56] mb-4">
                  {t.diagnostic.baselineOverline}
                </p>
                <h2 className="font-display text-2xl md:text-4xl font-normal tracking-tight text-[#0B0E14]">
                  {t.diagnostic.baselineTitle}
                </h2>
                <p className="text-base text-[#4A5568] mt-3 max-w-2xl">{t.diagnostic.baselineSubtitle}</p>
                <div className="mt-12 flex flex-col gap-12">
                  {content.baseline.map((item) => (
                    <div key={item.id} className="border-t border-[#E2DFD8] pt-8" data-testid={`baseline-${item.id}`}>
                      <p className="text-base md:text-lg text-[#0B0E14] leading-relaxed mb-6 max-w-2xl">
                        {item.question}
                      </p>
                      <div className={item.type === "scale" ? "grid grid-cols-5 gap-2 md:gap-3" : "flex flex-col gap-3"}>
                        {item.options.map((opt) => {
                          const selected = baseline[item.id] === opt.value;
                          if (item.type === "scale") {
                            return (
                              <button
                                key={opt.value}
                                onClick={() => selectBaseline(item.id, opt.value)}
                                data-testid={`baseline-option-${item.id}-${opt.value}`}
                                className={`flex flex-col items-center justify-start text-center px-1 py-4 border transition-colors ${
                                  selected
                                    ? "bg-[#0B0E14] text-white border-[#0B0E14]"
                                    : "bg-white text-[#4A5568] border-[#E2DFD8] hover:border-[#0B0E14]"
                                }`}
                              >
                                <span className="font-display text-lg font-medium">{opt.value}</span>
                                {opt.label && (
                                  <span className={`text-[10px] mt-2 leading-tight ${selected ? "text-white/80" : "text-[#718096]"}`}>
                                    {opt.label}
                                  </span>
                                )}
                              </button>
                            );
                          }
                          return (
                            <button
                              key={opt.value}
                              onClick={() => selectBaseline(item.id, opt.value)}
                              data-testid={`baseline-option-${item.id}-${opt.value}`}
                              className={`text-left px-5 py-4 border transition-colors ${
                                selected
                                  ? "bg-[#0B0E14] text-white border-[#0B0E14]"
                                  : "bg-white text-[#2C363F] border-[#E2DFD8] hover:border-[#0B0E14]"
                              }`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-16 flex items-center justify-between border-t border-[#E2DFD8] pt-8">
          <button
            onClick={back}
            disabled={step === 0}
            data-testid="back-btn"
            className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#0B0E14] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            {t.diagnostic.back}
          </button>

          {!isBaselineStep ? (
            <button
              onClick={next}
              data-testid="next-btn"
              className="inline-flex items-center gap-2 bg-[#0B0E14] text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#2C363F] transition-colors"
            >
              {t.diagnostic.continue}
              <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </button>
          ) : (
            <button
              onClick={finish}
              disabled={submitting}
              data-testid="see-results-btn"
              className="inline-flex items-center gap-2 bg-[#0B0E14] text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#2C363F] transition-colors disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {t.diagnostic.seeResults}
              {!submitting && <ArrowRight className="w-4 h-4" strokeWidth={1.75} />}
            </button>
          )}
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
